import type { TechlineAssetEntity, TechlineAssetStatus } from '../entities';

/** Processing statuses that represent visible (active) assets. */
const ACTIVE_ASSET_STATUSES: TechlineAssetStatus[] = ['complete', 'uploading'];

/**
 * Keep only entities that are complete or uploading.
 * Filters out errored, processing, or other non-visible assets.
 *
 * @param assets - the full list of asset entities
 * @returns only assets in an active status
 * @example
 * const visible = filterActiveAssetEntities(allAssets);
 */
export function filterActiveAssetEntities(assets: TechlineAssetEntity[]): TechlineAssetEntity[] {
    return assets.filter((asset) => ACTIVE_ASSET_STATUSES.includes(asset.status));
}

/**
 * Keep only case-level assets, excluding worksheet attachment assets.
 * Worksheet assets have a non-empty `worksheetSourceId` linking them to
 * a worksheet question submission. In-progress uploads (no server data
 * yet) are always included so they remain visible during upload.
 *
 * @param assets - the full list of asset entities
 * @returns only case-level assets and in-progress uploads
 * @example
 * const caseAssets = filterCaseLevelAssets(allAssets);
 */
export function filterCaseLevelAssets(assets: TechlineAssetEntity[]): TechlineAssetEntity[] {
    return assets.filter((asset) => asset.status === 'uploading' || !asset.isWorksheetAsset);
}

/**
 * Keep only asset entities that are still in the uploading status.
 * Used to identify assets that need polling for completion.
 *
 * @param assets - the list of asset entities to filter
 * @returns only assets still uploading
 * @example
 * const pending = filterUploadingAssets(allAssets);
 */
export function filterUploadingAssets(assets: TechlineAssetEntity[]): TechlineAssetEntity[] {
    return assets.filter((asset) => asset.status === 'uploading');
}
